import React, { useCallback, useState, useEffect, useContext } from "react";
import { Button } from "primereact/button";
import { useTranslation } from "react-i18next";
import { useOutletContext } from "react-router-dom";
import {
  calcCarbonFootprintData,
  F105032001Before,
  updateSensitivityAnalysis,
} from "../../../utility/service/carbonFootprint-service";
import { checkDpSuccess } from "../../../utility/service/tool-service";
import { SystemLoading } from "../../../store/loadingStore";
import { MessageContext, handleSuccessMessage } from "../../../store/messageStore";
import FormNumberInput from "../../../components/FormNumberInput";
import { FormTextarea } from "../../../components/FormTextarea";
import { Controller, useForm, useFieldArray } from "react-hook-form";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { Checkbox } from "primereact/checkbox";
import PrimeTableFooter from "../../../components/PrimeTableFooter";
import NoInfo from "../../../components/NoInfo";
import NumberUtil from "../../../utility/NumberUtil";
import { addFormValidator } from "../../../utility/validate/formValidator";

const SensitivityAnalysis = ({isEditable}) => {
  const {
    register,
    handleSubmit,
    watch,
    clearErrors,
    getValues,
    setValue,
    resetField,
    reset: resetForm,
    control,
    trigger,
    reset,
    formState: { errors, isValid, isDirty },
  } = useForm({
    defaultValues: {},
    //錯誤驗證時機
    mode: "all",
  });

  const { t } = useTranslation();
  const [formRule, setFormRule] = useState([]); //表單驗證 設備
  const { itemObj, setItemObj, action, setAction } = useOutletContext();
  const [, loading] = useContext(SystemLoading);
  const [, dispatch] = useContext(MessageContext);

  useEffect(() => {
    if (itemObj?.carbonFpId) {
      axios_F105032001Before();
      axios_sensitivityAnalysis();
    }
  }, [itemObj?.carbonFpId]);

  const setValidForm = useCallback(
    (formId) => {
      if (formRule?.length > 0) {
        const tmp = formRule?.find((item) => {
          return Object.keys(item)[0] === formId;
        });
        return tmp ? tmp[formId] : "";
      }
    },
    [formRule]
  );

  const { fields: dataFields } = useFieldArray({
    control,
    name: "carbonFootprintDatas",
  });

  const axios_F105032001Before = () => {
    F105032001Before({ carbonFpId: itemObj?.carbonFpId }).then((res) => {
      if (checkDpSuccess(res)) {
        setFormRule(processConstraints(res?.data?.RespBody?.constraints));
      }
    });
  };

  const axios_sensitivityAnalysis = () => {
    calcCarbonFootprintData({ carbonFpId: itemObj?.carbonFpId }).then((res) => {
      if (checkDpSuccess(res)) {
        let data = res?.data?.RespBody;
        setValue("sensitivityDeltaPct", data?.sensitivityDeltaPct);
        setValue("sensitivityReason", data?.sensitivityReason);
        setValue("carbonFootprintDatas", data?.carbonFootprintDatas);
      }
    });
  };

  const axios_updateSensitivityAnalysis = (reqBody) => {
    updateSensitivityAnalysis(reqBody).then((res) => {
      if (checkDpSuccess(res)) {
        handleSuccessMessage(dispatch, t("button.update") + " " + t("message.success"));
        axios_sensitivityAnalysis();
      }
    });
  };

  const processConstraints = (constraints) => {
    return constraints.map((item) => {
      if (item.type === "collection") {
        return { [item?.field]: processConstraints(item?.subObject.value) };
      } else {
        return addFormValidator(item?.field, constraints);
      }
    });
  };

  //避免觸發 按enter 後 就觸發 onSubmit 事件冒泡 給input 使用 <FormInput enterClick事件 ; 一般input onKeyDown事件
  const inputCheckKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
    }
  };

  // 增量標記模板
  const incrementalFlagTemplate = (rowData, options) => {
    return (
      <Checkbox
        checked={rowData?.incrementalFlag === "Y"}
        disabled={!isEditable}
        onChange={(e) => {
          const datas = getValues("carbonFootprintDatas") || [];
          // 找出 row 的 index（PrimeReact DataTable 會傳 options.rowIndex）
          const idx = options.rowIndex;
          // 複製一份資料
          const newDatas = [...datas];
          // 更新該列的 incrementalFlag
          newDatas[idx].incrementalFlag = e.checked ? "Y" : "N";
          // 存回去
          setValue("carbonFootprintDatas", newDatas, {
            shouldDirty: true,
            shouldValidate: true,
          });
        }}
      />
    );
  };

  const onSubmit = (data) => {
    let reqBody = {
      carbonFpId: itemObj?.carbonFpId,
      sensitivityDeltaPct: data.sensitivityDeltaPct,
      sensitivityReason: data.sensitivityReason,
      carbonFootprintDatas: data.carbonFootprintDatas,
    };
    axios_updateSensitivityAnalysis(reqBody);
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <div className="grid">
        {/* 數據增量(%) */}
        <div className="col-12 md:col-3 lg:col-3">
          <FormNumberInput
            withLabel={true}
            register={register}
            errors={errors}
            labelClass={`control-label required`}
            id="sensitivityDeltaPct"
            labelText={t("sensitivity_delta_pct")}
            disabled={!isEditable}
            enterClick={inputCheckKeyDown}
            rules={setValidForm(t("sensitivityDeltaPct"))}
            value={watch("sensitivityDeltaPct")}
            onChange={(newValue) => setValue("sensitivityDeltaPct", newValue)}
          />
        </div>
        {/* 增量原因 */}
        <div className="col-12 md:col-6 lg:col-7">
          <FormTextarea
            register={register}
            errors={errors}
            id="sensitivityReason"
            name="sensitivityReason"
            type="text"
            labelText={t("sensitivity_reason")}
            labelClass="w-full required"
            areaClass="w-full"
            rules={setValidForm(t("sensitivityReason"))}
            rows={1}
            disabled={!isEditable}
          />
        </div>
        <div className="col-12 md:col-3 lg:col-2 flex justify-content-end align-items-center">
          <Button
            label={t("sensitivityAnalysis.calculate")}
            icon="pi pi-calculator"
            className="p-button-primary"
            disabled={!(isValid && isDirty) || !isEditable}
          />
        </div>
      </div>
      <DataTable
        stripedRows
        value={dataFields}
        className="white-data-table hover-highlight no-shadow mt-1"
        tableStyle={{ minWidth: "50rem" }}
        footer={dataFields?.length > 0 && <PrimeTableFooter rowDataList={dataFields} isShowMore={false} />}
        emptyMessage={<NoInfo panel="table" size="md" />}
      >
        {/* 增量標記 */}
        <Column
          field="incrementalFlag"
          body={incrementalFlagTemplate}
          header={t("sensitivityAnalysis.incrementalFlag")}
          style={{ textAlign: "center" }}
          headerStyle={{ textAlign: "center" }}
        ></Column>

        <Column field="stageName" header={t("lifeCycleStage")}></Column>
        <Column field="dataName" header={t("activityDataName")}></Column>

        {/* 每單位數量（單位） */}
        <Column
          field="unitQuantity"
          body={(rowData, props) => {
            return (
              <>
                <div>{NumberUtil.formatWithCommas(rowData?.unitQuantity)}</div>
                <div>{rowData?.dataUnitName}</div>
              </>
            );
          }}
          header={
            <>
              {t("quantityPerUnit")}
              <br />({t("unit")})
            </>
          }
          style={{ textAlign: "right" }}
          headerStyle={{ textAlign: "right" }}
          headerClassName="right-header"
        ></Column>

        {/* 宣告單位 碳足跡 */}
        <Column
          field="unitValue"
          body={(rowData, props) => {
            return <div>{NumberUtil.formatWithCommas(rowData?.unitValue)}</div>;
          }}
          header={
            <>
              {t("carbon_footprint")}
            </>
          }
          style={{ textAlign: "right" }}
          headerStyle={{ textAlign: "right" }}
          headerClassName="right-header"
        ></Column>

        {/* 活動數據增量後碳足跡排放量 */}
        <Column
          field="increaseCfValue"
          body={(rowData, props) => {
            if (!rowData?.increaseCfValue) return null;
            return <div>{NumberUtil.formatWithCommas(rowData?.increaseCfValue)}</div>;
          }}
          header={<>{t('sensitivityAnalysis.increaseCfValue')}<br/>{t('carbon_footprint')}</>}
          style={{ textAlign: "right" }}
          headerStyle={{ textAlign: "right" }}
          headerClassName="right-header"
        ></Column>

        {/* 排放量增加比例 */}
        <Column
          field="increaseCfRatio"
          body={(rowData, props) => {
            if (!rowData?.increaseCfRatio) return null;
            return <div>{NumberUtil.formatWithCommas(rowData?.increaseCfRatio)}%</div>;
          }}
          header={<>{t('sensitivityAnalysis.increaseCfRatio')}</>}
          style={{ textAlign: "right" }}
          headerStyle={{ textAlign: "right" }}
          headerClassName="right-header"
        ></Column>
      </DataTable>
    </form>
  );
};

export default SensitivityAnalysis;
